import React from "react";
import {
  DocumentTextIcon,
  AcademicCapIcon,
  BeakerIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon
} from "@heroicons/react/24/outline";
import ScoreProgress from "./ScoreProgress";

/**
 * TemplateCard Component
 * Displays a template scoring card with status indicators
 *
 * @param {object} template - Template object with _id, name, type, items, etc.
 * @param {string} completionStatus - "not_started" | "partial" | "complete"
 * @param {function} onClick - Click handler function
 * @param {number} itemsCompleted - Number of items completed
 * @param {number} itemsTotal - Total number of items
 * @param {number} itemsNotApplicable - Number of items marked as N/A
 * @param {number} totalScore - Current total score
 * @param {number} maxPossibleScore - Maximum possible score for template
 */
const TemplateCard = ({
  template,
  completionStatus = "not_started",
  onClick,
  itemsCompleted = 0,
  itemsTotal = 0,
  itemsNotApplicable = 0,
  totalScore = 0,
  maxPossibleScore = 0,
}) => {
  const isCore = template?.type === "core";

  // Calculate percentage
  const percentage = maxPossibleScore > 0 ? (totalScore / maxPossibleScore) * 100 : 0;
  const itemsPercentage = itemsTotal > 0 ? (itemsCompleted / itemsTotal) * 100 : 0;

  // Theme configuration based on completion status
  const getThemeClasses = () => {
    switch (completionStatus) {
      case "complete":
        return {
          border: "border-green-400",
          gradient: "from-green-50 to-white",
          statusBg: "bg-green-500",
          statusText: "text-white",
          statusLabel: "✓ Completed",
          statusIcon: CheckCircleIcon,
          iconColor: "text-green-600",
          hover: "hover:border-green-500 hover:shadow-xl",
          bottomBar: "bg-gradient-to-r from-green-500 to-green-600",
        };
      case "partial":
        return {
          border: "border-yellow-400",
          gradient: "from-yellow-50 to-white",
          statusBg: "bg-yellow-500",
          statusText: "text-white",
          statusLabel: "⏳ In Progress",
          statusIcon: ClockIcon,
          iconColor: "text-yellow-600",
          hover: "hover:border-yellow-500 hover:shadow-xl",
          bottomBar: "bg-gradient-to-r from-yellow-500 to-yellow-600",
        };
      case "not_started":
      default:
        return {
          border: "border-red-400",
          gradient: "from-red-50 to-white",
          statusBg: "bg-red-500",
          statusText: "text-white",
          statusLabel: "✗ Not Started",
          statusIcon: ExclamationCircleIcon,
          iconColor: "text-red-600",
          hover: "hover:border-red-500 hover:shadow-xl",
          bottomBar: "bg-gradient-to-r from-red-500 to-red-600",
        };
    }
  };

  const theme = getThemeClasses();
  const StatusIcon = theme.statusIcon;

  return (
    <div
      onClick={onClick}
      className={`relative overflow-hidden rounded-xl border-2 transition-all duration-300 cursor-pointer ${
        theme.border
      } bg-gradient-to-br ${theme.gradient} ${theme.hover}`}
    >
      {/* Type Badge */}
      <div className="absolute top-3 right-3 z-10">
        <span
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
            isCore
              ? "bg-blue-100 text-blue-800 border border-blue-200"
              : "bg-gray-100 text-gray-700 border border-gray-200"
          }`}
        >
          {isCore ? (
            <AcademicCapIcon className="h-3 w-3 mr-1" />
          ) : (
            <BeakerIcon className="h-3 w-3 mr-1" />
          )}
          {isCore ? "Core" : "Supplementary"}
        </span>
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Template Name */}
        <div className="mb-4 pr-24">
          <div className="flex items-start space-x-2">
            <DocumentTextIcon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${theme.iconColor}`} />
            <h3 className="text-base font-semibold text-gray-900 leading-tight">
              {template?.name || "Untitled Template"}
            </h3>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Click to view and score items
          </p>
        </div>

        {/* Status Badge */}
        <div className="mb-4">
          <span
            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold ${
              theme.statusBg
            } ${theme.statusText}`}
          >
            <StatusIcon className="h-4 w-4 mr-1.5" />
            {theme.statusLabel}
          </span>
        </div>

        {/* Progress Info */}
        <div className="space-y-3">
          {/* Items Progress */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600">Items Progress</span>
            <div className="text-right">
              <span className="text-lg font-bold text-gray-900">
                {itemsCompleted}
              </span>
              <span className="text-sm text-gray-500"> / {itemsTotal}</span>
              <div className="text-xs font-medium text-gray-600 mt-0.5">
                ({itemsPercentage.toFixed(0)}%)
              </div>
            </div>
          </div>

          {/* N/A Items Count (if any) */}
          {itemsNotApplicable > 0 && (
            <div className="flex items-center justify-between bg-gray-100 rounded-lg px-3 py-2 border border-gray-200">
              <span className="text-xs font-medium text-gray-600">Marked as N/A</span>
              <div className="flex items-center space-x-1">
                <span className="text-sm font-bold text-gray-700">
                  {itemsNotApplicable}
                </span>
                <span className="text-xs text-gray-500">
                  {itemsNotApplicable === 1 ? 'item' : 'items'}
                </span>
              </div>
            </div>
          )}

          {/* Score Display */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600">Total Score</span>
            <div className="text-right">
              <span
                className={`text-2xl font-bold ${
                  totalScore > 0
                    ? isCore
                      ? "text-blue-700"
                      : "text-gray-700"
                    : "text-gray-400"
                }`}
              >
                {totalScore.toFixed(2)}
              </span>
              <span className="text-sm text-gray-500"> / {maxPossibleScore}</span>
              <div className="text-xs font-medium text-gray-600 mt-0.5">
                ({percentage.toFixed(1)}%)
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          {totalScore > 0 && <ScoreProgress value={percentage} showStatus={true} />}
        </div>
      </div>

      {/* Decorative Element */}
      <div className={`absolute bottom-0 left-0 w-full h-1 ${theme.bottomBar}`} />
    </div>
  );
};

export default TemplateCard;
